import React, { useCallback, useEffect, useState } from 'react';
import { HiOutlineExclamation } from 'react-icons/hi';
import Modal from './Modal';

/**
 * Confirmation de suppression adossee a l'analyse d'impact du serveur.
 *
 * Le serveur reste seul juge : il renvoie ce qui sera detruit, ce qui sera
 * conserve, et d'eventuels motifs de blocage. Tant que l'analyse n'est pas
 * revenue, le bouton de suppression reste inactif — on ne supprime jamais a
 * l'aveugle.
 */
export default function DeleteImpactModal({
  show,
  onClose,
  onConfirm,
  title,
  targetLabel,
  fetchImpact,
  confirmText = 'Supprimer definitivement',
}) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setImpact(null);
    try {
      const { data } = await fetchImpact();
      setImpact(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "L'analyse d'impact a echoue.");
    }
    setLoading(false);
  }, [fetchImpact]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const blocked = impact && impact.supprimable === false;

  const handleConfirm = async () => {
    setWorking(true);
    try {
      await onConfirm(impact);
    } finally {
      setWorking(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={title}
      icon={<HiOutlineExclamation style={{ color: 'var(--danger, #d64545)' }} />}
      size="md"
      footer={
        <>
          <button className="btn btn-outline-secondary btn-sm" onClick={onClose} disabled={working}>
            Annuler
          </button>
          <button
            className="btn btn-danger btn-sm"
            onClick={handleConfirm}
            disabled={loading || working || blocked || !impact}
          >
            {working ? 'Suppression…' : confirmText}
          </button>
        </>
      }
    >
      <p style={{ marginBottom: '1rem' }}>
        Vous êtes sur le point de supprimer <strong>{targetLabel}</strong>.
      </p>

      {loading && (
        <div className="text-muted small">
          <output className="spinner-border spinner-border-sm me-2" aria-label="Analyse" />
          Analyse des données rattachées…
        </div>
      )}

      {error && <div className="alert alert-danger py-2 small mb-0">{error}</div>}

      {impact && (
        <>
          {blocked && (
            <div className="alert alert-warning py-2 small">
              <strong>Suppression impossible en l'état.</strong>
              <ul className="mb-0 mt-1 ps-3">
                {impact.bloquants.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}

          <div className="small" style={{ fontWeight: 600, marginBottom: '0.35rem' }}>
            Conséquences de la suppression
          </div>
          <ul className="small ps-3" style={{ marginBottom: '0.75rem' }}>
            {impact.consequences.map((c) => <li key={c}>{c}</li>)}
          </ul>

          {!blocked && (
            <div className="alert alert-danger py-2 small mb-0">
              Cette action est irréversible.
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
